import React, { useState } from 'react';
import CustomQuoteIntake from '../../components/CustomQuoteIntake';
import GhlSurveyEmbed from '../../components/GhlSurveyEmbed';
import Seo from '../../components/Seo';
import { HOURS, PHONE_DISPLAY, PHONE_TEL } from '../../constants/brand';
import { ROUTES } from '../../enum/routes';

type IntakeMode = 'custom' | 'ghl';

export default function QuotePage() {
	const [mode, setMode] = useState<IntakeMode>('custom');

	return (
		<>
			<Seo
				title='Get a Free Quote | Epic Home Pros'
				description='Request a free remodel estimate from Epic Home Pros for kitchens, baths, ADUs, and outdoor living across Texas.'
				path={ROUTES.QUOTE}
			/>
			<section className='bg-forest-tint py-16 sm:py-24'>
				<div className='container-page'>
					<div className='mx-auto max-w-2xl text-center'>
						<div className='label-eyebrow'>Free Quote</div>
						<h1 className='h-display mt-3'>Let's talk about your project.</h1>
						<p className='mt-5 text-lg text-ink/75'>
							Answer a few quick questions to get started. Or call us directly
							at{' '}
							<a
								href={`tel:${PHONE_TEL}`}
								className='font-semibold text-forest hover:text-forest-hover'
							>
								{PHONE_DISPLAY}
							</a>
							.
						</p>
					</div>
				</div>
			</section>
			<section className='py-16 sm:py-20'>
				<div className='container-page grid grid-cols-1 gap-12 lg:grid-cols-12'>
					<aside className='quote-intro order-2 lg:order-1 lg:col-span-4'>
						<h2 className='font-serif text-2xl text-ink'>What to expect</h2>
						<ul className='mt-6 space-y-5'>
							<li className='flex gap-3'>
								<span className='mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-forest text-white'>
									<svg
										width='14'
										height='14'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='3'
										strokeLinecap='round'
										strokeLinejoin='round'
										aria-hidden='true'
									>
										<polyline points='20 6 9 17 4 12' />
									</svg>
								</span>
								<div>
									<div className='font-semibold text-ink'>
										Response within 24 hours
									</div>
									<p className='mt-1 text-sm text-ink/70'>
										We'll get back to you within one business day and help you
										take the next step.
									</p>
								</div>
							</li>
							<li className='flex gap-3'>
								<span className='mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-forest text-white'>
									<svg
										width='14'
										height='14'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='3'
										strokeLinecap='round'
										strokeLinejoin='round'
										aria-hidden='true'
									>
										<polyline points='20 6 9 17 4 12' />
									</svg>
								</span>
								<div>
									<div className='font-semibold text-ink'>On-site visit</div>
									<p className='mt-1 text-sm text-ink/70'>
										An on-site visit walks the space and talks through what's
										possible.
									</p>
								</div>
							</li>
							<li className='flex gap-3'>
								<span className='mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-forest text-white'>
									<svg
										width='14'
										height='14'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='3'
										strokeLinecap='round'
										strokeLinejoin='round'
										aria-hidden='true'
									>
										<polyline points='20 6 9 17 4 12' />
									</svg>
								</span>
								<div>
									<div className='font-semibold text-ink'>Free estimate</div>
									<p className='mt-1 text-sm text-ink/70'>
										Your estimate is free. You decide what happens next.
									</p>
								</div>
							</li>
							<li className='flex gap-3'>
								<span className='mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-forest text-white'>
									<svg
										width='14'
										height='14'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='3'
										strokeLinecap='round'
										strokeLinejoin='round'
										aria-hidden='true'
									>
										<polyline points='20 6 9 17 4 12' />
									</svg>
								</span>
								<div>
									<div className='font-semibold text-ink'>Clear next steps</div>
									<p className='mt-1 text-sm text-ink/70'>
										We'll schedule a visit to your home, understand the project,
										and prepare your estimate.
									</p>
								</div>
							</li>
						</ul>
						<div className='mt-10 rounded-lg border border-black/5 bg-cream p-6'>
							<div className='text-xs font-semibold uppercase tracking-wider text-ink/60'>
								Prefer to talk?
							</div>
							<a
								href={`tel:${PHONE_TEL}`}
								className='mt-2 block font-serif text-3xl text-forest hover:text-forest-hover'
							>
								{PHONE_DISPLAY}
							</a>
							<div className='mt-3 text-sm text-ink/70'>{HOURS}</div>
						</div>
					</aside>
					<div className='order-1 lg:order-2 lg:col-span-8'>
						<div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
							<p className='text-xs font-semibold uppercase tracking-wider text-ink/45'>
								Compare intake
							</p>
							<div
								className='inline-flex rounded-md border border-black/10 bg-cream p-1'
								role='tablist'
								aria-label='Quote intake method'
							>
								<button
									type='button'
									role='tab'
									aria-selected={mode === 'custom'}
									onClick={() => setMode('custom')}
									className={`rounded px-3 py-1.5 text-sm font-medium transition ${
										mode === 'custom'
											? 'bg-white text-forest shadow-sm'
											: 'text-ink/60 hover:text-ink'
									}`}
								>
									Custom intake
								</button>
								<button
									type='button'
									role='tab'
									aria-selected={mode === 'ghl'}
									onClick={() => setMode('ghl')}
									className={`rounded px-3 py-1.5 text-sm font-medium transition ${
										mode === 'ghl'
											? 'bg-white text-forest shadow-sm'
											: 'text-ink/60 hover:text-ink'
									}`}
								>
									GHL embed
								</button>
							</div>
						</div>
						{mode === 'custom' ? <CustomQuoteIntake /> : <GhlSurveyEmbed />}
					</div>
				</div>
			</section>
		</>
	);
}
